import { Check, ChevronRight, Dot } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { appConfig } from "@/config/app";
import { cx } from "@/lib/cx";

import floating from "./Floating.module.css";

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export const MenuGroup = DropdownMenu.Group;
export const MenuRadioGroup = DropdownMenu.RadioGroup;
export const MenuSub = DropdownMenu.Sub;

/** Menus flip above their trigger when there is no room below (10 §6). */
export function MenuContent({
  className,
  sideOffset = appConfig.overlayOffsetPx,
  align = "start",
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        sideOffset={sideOffset}
        align={align}
        collisionPadding={appConfig.overlayOffsetPx}
        className={cx(floating.panel, floating.list, "animate-in", className)}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

interface ItemExtras {
  icon?: ReactNode;
  /** Shortcut hint shown on the right, e.g. "Ctrl+K". */
  shortcut?: string | undefined;
  tone?: "default" | "danger" | undefined;
}

export function MenuItem({
  icon,
  shortcut,
  tone = "default",
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.Item> & ItemExtras) {
  return (
    <DropdownMenu.Item
      className={cx(floating.item, tone === "danger" && floating.danger, className)}
      {...props}
    >
      {icon !== undefined && <span aria-hidden="true">{icon}</span>}
      {children}
      {shortcut !== undefined && <span className={floating.shortcut}>{shortcut}</span>}
    </DropdownMenu.Item>
  );
}

export function MenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.CheckboxItem>) {
  return (
    <DropdownMenu.CheckboxItem
      className={cx(floating.item, floating.indented, className)}
      {...props}
    >
      <DropdownMenu.ItemIndicator className={floating.indicator}>
        <Check aria-hidden="true" />
      </DropdownMenu.ItemIndicator>
      {children}
    </DropdownMenu.CheckboxItem>
  );
}

export function MenuRadioItem({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.RadioItem>) {
  return (
    <DropdownMenu.RadioItem className={cx(floating.item, floating.indented, className)} {...props}>
      <DropdownMenu.ItemIndicator className={floating.indicator}>
        <Dot aria-hidden="true" />
      </DropdownMenu.ItemIndicator>
      {children}
    </DropdownMenu.RadioItem>
  );
}

export function MenuSubTrigger({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.SubTrigger>) {
  return (
    <DropdownMenu.SubTrigger className={cx(floating.item, className)} {...props}>
      {children}
      <ChevronRight aria-hidden="true" className={floating.shortcut} />
    </DropdownMenu.SubTrigger>
  );
}

export function MenuSubContent({
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.SubContent>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.SubContent
        collisionPadding={appConfig.overlayOffsetPx}
        className={cx(floating.panel, floating.list, "animate-in", className)}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className={floating.separator} />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className={floating.label}>{children}</DropdownMenu.Label>;
}
